/* eslint complexity: ["error", {max: 10}] -- onOpenTask, isCore extraction */
/* eslint max-lines: ["error", {max: 255}] -- Due to isCore threading */
/* eslint max-lines-per-function: ["error", {max: 190}] -- Due to long file */
import { useMemo, useRef } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  HomeAnalyticsEvent,
  TasksAnalyticsSources,
} from "@/constants/analytics/Analytics";
import { TaskSubcategoryToModalPathMap } from "@/constants/Tasks";
import { HomeTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useTaskSubDeepLink from "@/hooks/deepLinking/subDeepLinks/useTaskSubDeepLink";
import useCarouselUserChange from "@/hooks/useCarouselUserChange";
import useTasks from "@/hooks/useTasks";
import useWhyCompleteAssessments from "@/hooks/useWhyCompleteAssessments";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetLastUpdatedUsersQuery } from "@/store/api/tasks";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { useTaskDetailsModalDispatch } from "@/store/reducers/modals/taskDetailsModal";
import WhyCompleteAssessmentsAccordion from "@/ui/components/explore/WhyCompleteAssessmentsAccordion";
import TasksOverviewCard from "@/ui/components/tasks/TaskOverviewCard";
import TasksEmptyState from "@/ui/components/tasks/TasksEmptyState";
import { SectionHeader } from "@/ui/core";
import { CarouselWrapper, FlatList } from "@/ui/layout";
import type { FlashListRef } from "@/ui/layout/list/FlatList";
import { joinTestIDs } from "@/utils/string.util";
import {
  getPreferredFirstName,
  getTabSelectorOptions,
} from "@/utils/user.util";

const getPatientName = ({
  appUserId,
  patientId,
  proxyPatients,
  selectedUserId,
}: {
  appUserId: string;
  patientId: string;
  proxyPatients?: LinkedPatientProfile[];
  selectedUserId: string;
}) => {
  const taskPatient = proxyPatients?.find(
    (patient: LinkedPatientProfile) =>
      patient.externalId === patientId && selectedUserId === appUserId,
  );

  if (!taskPatient) return;
  return getPreferredFirstName({
    chosenName: taskPatient.chosenName,
    firstName: taskPatient.firstName,
  });
};

const TasksCarousel = () => {
  const { track } = useAnalytics();
  const { externalId: appUserId, isPatient } = useAppUserSelector();
  const proxyPatients = useAppUserProxyPatientsSelector();

  const { captureMessage } = useAppMonitor();
  const flatListRef = useRef<FlashListRef<TaskItem> | null>(null);

  const { handleUserChange, selectedUserId } = useCarouselUserChange<TaskItem>({
    analyticsComponent: AnalyticsComponent.Tab,
    analyticsSource: TasksAnalyticsSources.Section,
    scrollableRef: flatListRef,
  });

  const { setTaskDetailsData } = useTaskDetailsModalDispatch();

  const { appUserCount, isLoading, patientsCounters, tasks } = useTasks({
    appUserId,
    isPatient,
    linkedPatients: proxyPatients,
  });
  const { isWhyCompleteAssessmentsLoading, isWhyCompleteAssessmentsVisible } =
    useWhyCompleteAssessments({
      tasks,
    });
  const currentUserTasks = useMemo(() => {
    return tasks[selectedUserId] ?? [];
  }, [selectedUserId, tasks]);

  const surveyIds = useMemo(() => {
    const linkedSurveyIds = currentUserTasks
      .filter((el) => !isNil(el.linkedSurveyId))
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- previous line guards against this, but typescript compiler can't tell
      .map((el) => el.linkedSurveyId!);
    return linkedSurveyIds;
  }, [currentUserTasks]);

  const { currentData: lastUpdatedUsers } =
    useGetLastUpdatedUsersQuery(surveyIds);

  const tabOptions = useMemo(() => {
    return getTabSelectorOptions({
      appUserCount,
      appUserId,
      isAppUserPatient: isPatient,
      patients: proxyPatients,
      patientsCounters,
      shouldIncludeSelf: !isPatient,
    });
  }, [appUserCount, appUserId, isPatient, patientsCounters, proxyPatients]);

  const onOpenTask = ({
    isDeepLink = false,
    item: {
      estimatedDuration,
      familyAssigneeUsers,
      linkedNoteId,
      linkedSurveyId,
      patientId,
      taskDescription,
      taskExternalId,
      taskName,
      taskStatus,
      taskSubCategoryName,
    },
  }: {
    isDeepLink?: boolean;
    item: TaskItem;
  }) => {
    const lastUpdatedInfo = lastUpdatedUsers?.find(
      (info: LastUpdatedUserResponse) => {
        return info.responseExternalId === linkedSurveyId;
      },
    );
    const lastUpdatedByUserId = lastUpdatedInfo?.lastUpdatedByUserId;
    const isCore = lastUpdatedInfo?.isCore;
    const pathName =
      TaskSubcategoryToModalPathMap[taskSubCategoryName] ||
      TaskSubcategoryToModalPathMap["All"];

    if (!pathName) {
      captureMessage(
        `Task type ${taskSubCategoryName} does not have a modal path`,
      );
      return;
    }

    const lastUpdatedByUserType = familyAssigneeUsers?.find(
      (user: FamilyAssigneeUser) => user.externalId === lastUpdatedByUserId,
    )?.type;

    setTaskDetailsData({
      description: taskDescription,
      duration: estimatedDuration,
      isCore,
      lastUpdatedByUserId,
      lastUpdatedByUserType,
      linkedNoteId,
      linkedSurveyId,
      patientExternalId: patientId,
      patientName: getPatientName({
        appUserId,
        patientId,
        proxyPatients,
        selectedUserId,
      }),
      selectedUserId,
      taskId: taskExternalId,
      taskStatus,
      taskSubCategoryName,
      taskType: taskSubCategoryName,
      title: taskName,
    });

    if (!isDeepLink) {
      track(HomeAnalyticsEvent.TaskCardPressed, {
        [AnalyticsFieldName.Component]: pathName,
        [AnalyticsFieldName.Source]: TasksAnalyticsSources.Section,
        [AnalyticsFieldName.Title]: taskName,
      });
    }
    router.navigate(pathName);
  };

  useTaskSubDeepLink({
    handleUserChange,
    onOpenTask,
    selectedUserId,
    tasks,
  });

  const header = (
    <SectionHeader
      appUserBadgeCount={appUserCount}
      appUserId={appUserId}
      initialUserId={selectedUserId}
      isAppUserSupport={!isPatient}
      isInlineHeader
      onUserChange={handleUserChange}
      testID={HomeTestID.Tasks}
      title="To-dos"
      userTabs={tabOptions}
    />
  );
  const emptyListComponent = () => <TasksEmptyState />;
  return (
    <CarouselWrapper header={header}>
      <FlatList<TaskItem>
        data={currentUserTasks}
        emptyListComponent={emptyListComponent}
        isDataLoading={isLoading}
        isHorizontal
        keyExtractor={(item) => item.taskExternalId}
        listRef={flatListRef}
        renderItem={({ item }) => {
          const {
            estimatedDuration,
            patientId,
            taskExternalId: taskId,
            taskName,
            taskStatus,
          } = item;
          const patientName = getPatientName({
            appUserId,
            patientId,
            proxyPatients,
            selectedUserId,
          });

          return (
            <TasksOverviewCard
              duration={estimatedDuration}
              onPress={() => onOpenTask({ item })}
              patientName={patientName}
              taskId={taskId}
              taskStatus={taskStatus}
              taskTitle={taskName}
            />
          );
        }}
        shouldRemoveContentVerticalPadding
        testID={joinTestIDs(HomeTestID.Tasks, "scrollview")}
      />
      {!isWhyCompleteAssessmentsLoading && isWhyCompleteAssessmentsVisible && (
        <WhyCompleteAssessmentsAccordion />
      )}
    </CarouselWrapper>
  );
};

export default TasksCarousel;
